import { useState, type KeyboardEvent, type MouseEvent } from 'react';
import { useLocation, useParams, Link, Navigate } from 'react-router-dom';
import { familyForProduct, productPath, resolveProduct, shippingLineFor, installmentLineFor, FAMILY_MANUALS } from '@evi/store-kit';
import { useCatalog } from '@/hooks/useCatalog';
import { useCart } from '@/lib/cartStore';
import { formatUsd } from '@/lib/api';
import { ManualDownload } from '@/components/ManualDownload';
import { usePrefersReducedMotion } from '@/hooks/useBrowserOnly';
import { isBuyable, productAvailability, productDescription, productSpecs } from '@/seo/productFacts';
import { selectedVariantId } from './productVariant';
import { NotFoundPage } from './NotFoundPage';
import styles from './ProductDetailPage.module.css';

/** Product detail: resolves the family from the cached catalog (same engine as
 *  the driver store), variant select, price + shipping/installment copy, the DC
 *  commissioning line, add-to-cart. Rendered on the server first: name, part
 *  number, description, specs, price, currency, availability and image are in
 *  the first response, and the structured data (seo/productFacts.ts) reads the
 *  same values. */
export function ProductDetailPage() {
  const { param = '' } = useParams<{ param: string }>();
  const { search, hash } = useLocation();
  const { data: catalog, isLoading, isError } = useCatalog();
  const add = useCart((s) => s.add);
  // The chip the visitor picked, with the product URL it was picked on, so a
  // new product link opens on its own variant.
  const [chosen, setChosen] = useState<{ url: string; id: string } | null>(null);
  const [added, setAdded] = useState(false);
  const [filmReady, setFilmReady] = useState(false);
  const reducedMotion = usePrefersReducedMotion();

  if (isLoading) return <div className={styles.state}>Loading…</div>;
  if (isError) return <div className={styles.state}>Could not load the catalog.</div>;

  const found = catalog ? resolveProduct(param, catalog) : null;
  if (!catalog || !found) return <NotFoundPage />;
  // The server answers an old URL with a 301 before it renders. This covers the rest: an old link followed inside the app,
  // and the page the server sends when it has no catalog. Only the browser sees the query string, so it carries it over.
  if (!found.canonical) return <Navigate replace to={{ pathname: productPath(found.product), search, hash }} />;
  const { product } = found;

  const family = familyForProduct(product, catalog);
  const selectedId = selectedVariantId(family, product.id, chosen?.url === product.id ? chosen.id : null);
  const selected =
    family.variants.find((v) => v.product.id === selectedId)?.product ?? product;
  // Descriptions may carry multiple paragraphs separated by a blank line
  // (the DC families do: overview, connector choice, warranty). Split so each
  // renders as its own paragraph rather than one run-on block.
  const descParagraphs = productDescription(selected, family)
    .split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  const availability = productAvailability(selected);
  const buyable = isBuyable(selected);
  const specs = productSpecs(selected);
  // Download-the-manual row keys on FAMILY_MANUALS (no entry, no row).
  const manual = FAMILY_MANUALS[family.key];

  const onAdd = () => {
    add(selected.id, 1);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1600);
  };

  // A chip is a real link to its variant's page, for crawlers and for opening in
  // a new tab; a plain click picks the variant here, as a button did.
  const choose = (e: MouseEvent | KeyboardEvent, id: string) => {
    e.preventDefault();
    setChosen({ url: product.id, id });
  };

  return (
    <div className={styles.page}>
      <div className={styles.media}>
        {family.media?.hero ? (
          <img src={family.media.hero} alt={selected.name} />
        ) : (
          <div className={styles.mediaFallback} aria-hidden />
        )}
        {/* The still is what the first response carries; the film fades in over it. */}
        {family.media?.film && !reducedMotion && (
          <video
            className={filmReady ? `${styles.film} ${styles.filmReady}` : styles.film}
            src={family.media.film}
            poster={family.media.poster}
            autoPlay
            muted
            loop
            playsInline
            onCanPlay={() => setFilmReady(true)}
          />
        )}
        {family.media?.caption && <p className={styles.caption}>{family.media.caption}</p>}
      </div>

      <div className={styles.info}>
        <p className={styles.crumb}>
          <Link to="/">Store</Link>
        </p>
        <h1 className={styles.title}>{family.title}</h1>
        {selected.name !== family.title && <p className={styles.skuName}>{selected.name}</p>}
        {descParagraphs.map((para) => (
          <p key={para.slice(0, 32)} className={styles.desc}>{para}</p>
        ))}

        {family.variants.length > 1 && (
          <div className={styles.variants} role="radiogroup" aria-label="Options">
            {family.variants.map((v) => (
              <a
                key={v.product.id}
                href={productPath(v.product)}
                role="radio"
                aria-checked={v.product.id === selectedId}
                className={
                  v.product.id === selectedId
                    ? `${styles.variant} ${styles.variantActive}`
                    : styles.variant
                }
                onClick={(e) => {
                  // Ctrl or Cmd click opens the variant's own page in a new tab.
                  if (!e.metaKey && !e.ctrlKey) choose(e, v.product.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === ' ') choose(e, v.product.id);
                }}
              >
                <span className={styles.variantLabel}>
                  {v.label}
                  {v.product.recommended && (
                    <span className={styles.recommended}>Recommended</span>
                  )}
                </span>
                <span className={styles.variantPrice}>{formatUsd(v.product.price_cents)}</span>
              </a>
            ))}
          </div>
        )}

        <div className={styles.priceRow}>
          <span className={styles.price}>{formatUsd(selected.price_cents)}</span>
          <span className={styles.currency}>USD</span>
        </div>
        <p className={styles.availability}>{availability.label}</p>
        {buyable && <p className={styles.line}>{shippingLineFor(selected)}</p>}
        {buyable && <p className={styles.line}>{installmentLineFor(selected.price_cents)}</p>}
        {selected.lead_time_label && <p className={styles.lead}>{selected.lead_time_label}</p>}

        {buyable ? (
          <>
            <button type="button" className="eviBtn eviBtnPrimary" onClick={onAdd}>
              {added ? 'Added to cart' : 'Add to cart'}
            </button>
            <p className={styles.cartLink}>
              <Link to="/cart">View cart</Link>
            </p>
          </>
        ) : (
          // Checkout refuses a quote-flow product, so it never reaches the cart.
          <Link to="/commercial-dc#talk-to-sales" className={`eviBtn eviBtnPrimary ${styles.quoteCta}`}>
            Talk to sales
          </Link>
        )}

        <section className={styles.specs} aria-label="Specifications">
          <h2 className={styles.specsTitle}>Specifications</h2>
          <dl className={styles.specList}>
            {specs.map((s) => (
              <div key={s.label} className={styles.specRow}>
                <dt>{s.label}</dt>
                <dd>{s.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Same mechanism as the in-app store, adapted for the public shop. */}
        {manual && <ManualDownload manual={manual} />}
      </div>
    </div>
  );
}
